import classnames from 'classnames'
import throttle from 'lodash.throttle'
import * as React from 'react'

import { Delayed } from '../../../main/components/delayed'
import { Spinner } from '../../../main/components/spinner'
import { ISettings } from '../constants/settings'
import LayoutGenerator from '../layout-generator'
import mapActionCreators from '../store/action-creators'
import Treemap from '../treemap'
import { MapDataIndustry, MapDataNode, MapDataRoot, MapDataSector, MapSubtype, MapSubtypeId, MapType } from '../types'
import * as mapUtils from '../utils'
import Canvas from './Canvas'

interface MapProps {
  className?: string
  isLoadingTreemap?: boolean
  isLoadingData?: boolean
  treemap?: Treemap
  setTreemap: (treemap: Treemap) => void
  type: MapType
  subtype: MapSubtype
  data?: MapDataRoot
  settings?: ISettings
  showETF?: boolean
  onNodeClick?: (
    event: React.MouseEvent,
    node?: MapDataNode,
    industry?: MapDataIndustry,
    sector?: MapDataSector
  ) => void
  zoom?: boolean
  zoomOnWheel?: boolean
  legend?: boolean
  hover?: boolean
}

export function Map({
  className,
  isLoadingTreemap,
  isLoadingData,
  treemap,
  setTreemap,
  type,
  subtype,
  data,
  settings,
  showETF,
  onNodeClick,
  zoom,
  zoomOnWheel,
  legend,
  hover,
}: MapProps) {
  const [generator, setGenerator] = React.useState<LayoutGenerator>()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const prevSubtype = React.useRef<MapSubtypeId>(subtype.id)

  /**
   * Construct the layout when data is ready and when type changes. Do not change layout on subtype
   * change as it is not necessary.
   */
  React.useEffect(() => {
    const containerElement = containerRef.current

    if (!containerElement || isLoadingData) {
      return
    }

    const isSmall = mapUtils.getIsSmall()
    const size = mapUtils.getSize(containerElement, type.type, isSmall)
    const mapSettings = settings ?? mapUtils.getSettingsForMapType(type.type, isSmall)
    const generator = new LayoutGenerator(size.width, size.height, type.type, mapSettings, isSmall)

    setGenerator(generator)
    window.MAP_EXPORT = generator
  }, [data, isLoadingData, type.type, settings])

  /**
   * Create a new Treemap instance when the layout changes
   */
  React.useEffect(() => {
    if (!generator || !data || generator.type === treemap?.type) return

    const treemapObj = new Treemap({
      data: generator.getLayout(data, { showETF }),
      width: generator.width,
      height: generator.height,
      settings: generator.settings,
      isSmall: generator.isSmall,
      scaleId: subtype.scaleId,
      countIndustryPerf: subtype.countIndustryPerf,
      countSectorPerf: generator.isSmall && subtype.countSectorPerf,
      type: type.type,
      subtype: subtype.id,
    })

    setTreemap(treemapObj)
    // Run this only when layout changes, that means we need a new treemap. Otherwise reuse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generator])

  /**
   * Update the layout when the subtype changes
   */
  React.useEffect(() => {
    if (!treemap || prevSubtype.current === subtype.id) return

    // Set prev type so we don’t run this effect accidentally again
    prevSubtype.current = subtype.id

    treemap.subtype = subtype.id
    treemap.scaleId = subtype.scaleId
    treemap.countIndustryPerf = subtype.countIndustryPerf
    treemap.countSectorPerf = treemap.isSmall && subtype.countIndustryPerf
  }, [subtype, treemap])

  React.useEffect(() => {
    if (data && generator && treemap) {
      const layout = generator.getLayout(data, { showETF })
      mapActionCreators.updateLayout(treemap, { width: generator.width, height: generator.height, data: layout })
    }
    // Only run when Show ETFs change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showETF])

  /**
   * Recalculate layout when the window size changes
   */
  React.useEffect(() => {
    const containerElement = containerRef.current

    if (!generator || !containerElement || !treemap || !data) return

    const resize = throttle(() => {
      const size = mapUtils.getSize(containerElement, generator.type, generator.isSmall)

      generator.width = size.width
      generator.height = size.height

      if (size.width !== treemap.width && size.height !== treemap.height) {
        treemap.zoom.scale(1)
        const layout = generator.getLayout(data, { showETF })
        mapActionCreators.updateLayout(treemap, { width: generator.width, height: generator.height, data: layout })
      }
    }, 100)

    window.addEventListener('resize', resize)

    return () => {
      window.removeEventListener('resize', resize)
    }
  }, [generator, treemap, data, showETF])

  return (
    <div id="map" className={className}>
      <div ref={containerRef} id="body" className={classnames('relative', { 'pointer-events-none': isLoadingTreemap })}>
        {isLoadingTreemap && (
          <Delayed>
            <div className="flex h-110 items-center justify-center">
              <Spinner />
            </div>
          </Delayed>
        )}
        {treemap && (
          <Canvas
            alt={document.title}
            treemap={treemap}
            scale={{
              id: subtype.scaleId,
              name: subtype.name,
              tooltip: subtype.tooltip,
            }}
            onNodeClick={onNodeClick}
            zoom={zoom}
            zoomOnWheel={zoomOnWheel}
            legend={legend}
            hover={hover}
          />
        )}
      </div>
    </div>
  )
}
